import { yupResolver } from "@hookform/resolvers/yup";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { LoadingButton } from "@mui/lab";
import {
    Button,
    Dialog,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    Stack,
    TextField,
    Typography,
    useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import defaultAxios, { AxiosError } from "axios";
import qs from "query-string";
import { useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useMutation, useQueryClient } from "react-query";
import Swal from "sweetalert2";
import * as yup from "yup";
import { ErrorFieldResponse } from "../../constants/types";
import { useAuth } from "../../context/authContext";
import axios from "../../services/axios";
import useUser from "../../services/queries/useUser";

interface IModalImportMemberProps {
    open: boolean;
    onClose: () => void;
}

interface IValuesImport {
    ukmId: string;
    file: File | null;
}

const schema = yup.object({
    ukmId: yup.string(),
    berkas: yup.mixed(),
});

const ModalImportMember = ({ open, onClose }: IModalImportMemberProps) => {
    const theme = useTheme();
    const isPhoneScreen = useMediaQuery(theme.breakpoints.between("xs", "sm"));
    const isDesktopScreen = useMediaQuery(theme.breakpoints.up("md"));
    const { ukmIdUser } = useAuth();
    const { data: user } = useUser();
    const [selectUkm, setSelectUkm] = useState<any>();

    const initialValues: IValuesImport = useMemo(
        () => ({
            ukmId: selectUkm ?? Number(ukmIdUser && ukmIdUser[0]),
            file: null,
        }),
        [selectUkm, ukmIdUser],
    );

    const {
        handleSubmit,
        control,
        reset,
        setValue,
        setError,
        clearErrors,
        formState: { errors },
    } = useForm<IValuesImport>({
        resolver: yupResolver(schema),
        defaultValues: initialValues,
    });

    const queryClient = useQueryClient();
    const mutation = useMutation({
        mutationFn: async (formData: FormData) => {
            const res = await axios.post(`/api/import-members`, formData, {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            });
            return res.data;
        },
    });

    const onSubmit = (values: IValuesImport) => {
        console.log(values);

        const formData = new FormData();
        formData.append("ukmId", values.ukmId);
        formData.append("file", values.file ? (values.file as any) : "");
        // const { data } = await axios.post<DefaultResponse>(`/api/importitem`, {
        //     berkas: values.berkas,
        // });
        mutation.mutate(formData, {
            onSuccess: () => {
                queryClient.invalidateQueries("list-members");
                Swal.fire({
                    title: "Member berhasil ditambah",
                    position: "top-end",
                    showConfirmButton: false,
                    icon: "success",
                    toast: true,
                    timer: 3000,
                    timerProgressBar: true,
                    showCloseButton: true,
                    customClass: {
                        container: "my-swal",
                    },
                });
                onClose();
                reset(initialValues);
            },
            onError: (error) => {
                if (defaultAxios.isAxiosError(error)) {
                    const serverError = error as AxiosError<
                        ErrorFieldResponse | undefined
                    >;
                    if (serverError && serverError?.response) {
                        console.log(
                            "Ini message",
                            serverError.response.data?.message,
                        );
                        Swal.fire({
                            title: `${serverError.response.data?.message}`,
                            position: "top-end",
                            showConfirmButton: false,
                            icon: "error",
                            toast: true,
                            timer: 3000,
                            timerProgressBar: true,
                            showCloseButton: true,
                            customClass: {
                                container: "my-swal",
                            },
                        });
                    }
                }
                onClose();
                console.error(error);
            },
        });
    };

    const [isLoading, setIsLoading] = useState(false);

    const downloadTemplate = async () => {
        setIsLoading(true);
        try {
            const api = qs.stringifyUrl({
                url: "api/export-members",
                query: {
                    ukmId: selectUkm ?? Number(ukmIdUser && ukmIdUser[0]),
                },
            });
            const { data } = await axios.get(api, {
                responseType: "blob",
            });
            const file = new Blob([data], {
                type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;",
            });
            const fileURL = URL.createObjectURL(file);
            const link = document.createElement("a");
            link.href = fileURL;
            link.setAttribute("download", `Template_Pengisian_Member_Setos`); //or any other extension
            document.body.appendChild(link);
            link.click();
            setIsLoading(false);
        } catch (error) {
            console.error(error);
            Swal.fire({
                title: "Terjadi Kesalahan!",
                position: "top-end",
                showConfirmButton: false,
                icon: "error",
                toast: true,
                timer: 3000,
                timerProgressBar: true,
                showCloseButton: true,
                customClass: {
                    container: "my-swal",
                },
            });
        }
    };

    useEffect(() => {
        if (Number(localStorage.getItem(`user${user?.userId}`))) {
            setSelectUkm(Number(localStorage.getItem(`user${user?.userId}`)));
        }
    }, [user?.userId]);

    return (
        <>
            <Dialog
                maxWidth="sm"
                fullScreen={isPhoneScreen}
                fullWidth={true}
                open={open}
                onClose={onClose}
                PaperProps={{
                    sx: {
                        borderRadius: 1,
                    },
                }}
            >
                <DialogTitle>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <IconButton onClick={onClose}>
                            <ArrowBackIcon />
                        </IconButton>
                        <Typography variant="h6" fontWeight="bold">
                            Tambah Member Excel
                        </Typography>
                    </Stack>
                </DialogTitle>
                <DialogContent>
                    {/* <code>{JSON.stringify(values)}</code> */}
                    <Typography variant="h6" fontWeight="bold">
                        Template Pengisian
                    </Typography>
                    <Typography
                        variant="body1"
                        color="#545E6A"
                        sx={{ maxWidth: "360px" }}
                    >
                        Unduh template terlebih dahulu sebelum mengunggah berkas
                    </Typography>
                    <LoadingButton
                        loading={isLoading}
                        loadingPosition="center"
                        onClick={downloadTemplate}
                        variant="outlined"
                        size="large"
                        sx={{ marginY: 4 }}
                    >
                        Unduh Template
                    </LoadingButton>
                    <Typography variant="h6" fontWeight="bold">
                        Unggah Berkas
                    </Typography>
                    <Grid container alignItems="center" marginY={2}>
                        <Grid item xs={12}>
                            <Typography variant="body2" color="#808080">
                                Berkas Stok
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <Controller
                                name="file"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        id="file"
                                        fullWidth
                                        type="file"
                                        variant="outlined"
                                        inputProps={{
                                            accept: ".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel",
                                        }}
                                        error={Boolean(errors.file)}
                                        helperText={
                                            errors.file
                                                ? errors.file.message
                                                : " "
                                        }
                                        {...field}
                                        onChange={(event: any) => {
                                            if (
                                                event.target.files[0].size <
                                                512000
                                            ) {
                                                console.log(
                                                    event.target.files[0],
                                                );
                                                setValue(
                                                    "file",
                                                    event.target.files[0],
                                                );
                                                clearErrors("file");
                                            } else {
                                                event.target.value = "";
                                                setError("file", {
                                                    type: "manual",
                                                    message:
                                                        "Ukuran berkas harus kurang dari 500KB",
                                                });
                                            }
                                        }}
                                        value={undefined}
                                    />
                                )}
                            />
                        </Grid>
                    </Grid>
                    <Stack
                        direction="row"
                        spacing={isDesktopScreen ? 0 : 2}
                        alignItems="center"
                    >
                        <Button
                            variant="outlined"
                            onClick={() => onClose()}
                            fullWidth
                            sx={{ display: { xs: "block", md: "none" } }}
                        >
                            <Typography>Kembali</Typography>
                        </Button>
                        <LoadingButton
                            loading={mutation.isLoading}
                            loadingPosition="center"
                            variant="contained"
                            onClick={handleSubmit(onSubmit)}
                            type="submit"
                            fullWidth={isDesktopScreen ? false : true}
                        >
                            Unggah
                        </LoadingButton>
                    </Stack>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default ModalImportMember;
